import { FR_ER, FR_IR, FR_RE } from './french';
import { compound, type LanguageTenses, type Tense } from './tenses';

// ─────────────────────────────────────────────────────────────────────────────
// French: every tense beyond the present (which has its own -er/-ir/-re
// topics). Regular verbs of the three groups only.
//
// Spelling kept: manger → mangeais/mangeai (e before a/o), commencer →
// commençais/commençai (ç before a/o), étudier → étudiions.
// Compound tenses: arriver, entrer, descendre take être, and accept every
// agreement (allé / allée / allés / allées).
// ─────────────────────────────────────────────────────────────────────────────

const ALL = { ...FR_ER, ...FR_IR, ...FR_RE };
const ETRE_VERBS = new Set(['arriver', 'entrer', 'descendre']);

type G = 'er' | 'ir' | 're';
const group = (v: string): G => (v in FR_ER ? 'er' : v in FR_IR ? 'ir' : 're');
const root = (v: string) => v.slice(0, -2);

/** Attach an ending, keeping manger/commencer's soft sound before a / o. */
function attach(stem: string, ending: string): string {
  if (/^[aoâ]/.test(ending)) {
    if (stem.endsWith('g')) return stem + 'e' + ending;
    if (stem.endsWith('c')) return stem.slice(0, -1) + 'ç' + ending;
  }
  return stem + ending;
}

/** The "nous" stem the imparfait and subjunctive build on. */
const longStem = (v: string) => (group(v) === 'ir' ? root(v) + 'iss' : root(v));
const futureStem = (v: string) => (group(v) === 're' ? v.slice(0, -1) : v);

const participle = (v: string) => root(v) + ({ er: 'é', ir: 'i', re: 'u' } as const)[group(v)];
const PLURAL = [false, false, false, true, true, true];

/** All the agreements an être participle can take for this person. */
function agreeing(v: string, i: number): string[] {
  const pp = participle(v);
  if (!ETRE_VERBS.has(v)) return [pp];
  if (i === 4) return [pp, pp + 'e', pp + 's', pp + 'es'];                    // vous
  return PLURAL[i] ? [pp + 's', pp + 'es'] : [pp, pp + 'e'];
}

const AUX = {
  pres: { avoir: ['ai', 'as', 'a', 'avons', 'avez', 'ont'], etre: ['suis', 'es', 'est', 'sommes', 'êtes', 'sont'] },
  imp: { avoir: ['avais', 'avais', 'avait', 'avions', 'aviez', 'avaient'], etre: ['étais', 'étais', 'était', 'étions', 'étiez', 'étaient'] },
  fut: { avoir: ['aurai', 'auras', 'aura', 'aurons', 'aurez', 'auront'], etre: ['serai', 'seras', 'sera', 'serons', 'serez', 'seront'] },
  cond: { avoir: ['aurais', 'aurais', 'aurait', 'aurions', 'auriez', 'auraient'], etre: ['serais', 'serais', 'serait', 'serions', 'seriez', 'seraient'] },
  subj: { avoir: ['aie', 'aies', 'ait', 'ayons', 'ayez', 'aient'], etre: ['sois', 'sois', 'soit', 'soyons', 'soyez', 'soient'] },
  ps: { avoir: ['eus', 'eus', 'eut', 'eûmes', 'eûtes', 'eurent'], etre: ['fus', 'fus', 'fut', 'fûmes', 'fûtes', 'furent'] },
};

const comp = (t: keyof typeof AUX) => (v: string, i: number) =>
  compound(AUX[t][ETRE_VERBS.has(v) ? 'etre' : 'avoir'][i], agreeing(v, i));

const IMP = ['ais', 'ais', 'ait', 'ions', 'iez', 'aient'];
const FUT = ['ai', 'as', 'a', 'ons', 'ez', 'ont'];
const SUBJ = ['e', 'es', 'e', 'ions', 'iez', 'ent'];
const PS_ER = ['ai', 'as', 'a', 'âmes', 'âtes', 'èrent'];
const PS_IR = ['is', 'is', 'it', 'îmes', 'îtes', 'irent'];
const PRESENT_PLURAL = { er: ['ons', 'ez'], ir: ['issons', 'issez'], re: ['ons', 'ez'] };

/** je → j' before a vowel or mute h; que + il → qu'il. */
const vowel = (s: string) => /^[aeiouyhéèêàâîôû]/i.test(s);
const elide = (_i: number, answer: string, who: string) => (who === 'je' && vowel(answer) ? "j'" : who);
function que(i: number, answer: string, who: string): string {
  const w = elide(i, answer, who);
  return /^(il|elle|on|ils|elles)$/.test(w) ? `qu'${w}` : `que ${w}`;
}

function imperative(v: string, i: number): string[] | null {
  const g = group(v);
  if (i === 1) return [g === 'er' ? root(v) + 'e' : g === 'ir' ? root(v) + 'is' : root(v) + 's'];
  if (i === 3) return [attach(root(v), PRESENT_PLURAL[g][0])];
  if (i === 4) return [root(v) + PRESENT_PLURAL[g][1]];
  return null;
}

const TENSES: Tense[] = [
  { id: 'imparfait', title: 'Imparfait', blurb: 'je parlais, nous finissions…', verbs: ALL,
    forms: (v, i) => [attach(longStem(v), IMP[i])], who: elide },
  { id: 'futur', title: 'Futur simple', blurb: 'je parlerai, ils vendront…', verbs: ALL,
    forms: (v, i) => [futureStem(v) + FUT[i]], who: elide },
  { id: 'conditionnel', title: 'Conditionnel présent', blurb: 'je parlerais, nous finirions…', verbs: ALL,
    forms: (v, i) => [futureStem(v) + IMP[i]], who: elide },
  { id: 'passe_compose', title: 'Passé composé', blurb: 'j’ai parlé, elle est arrivée…', verbs: ALL,
    forms: comp('pres'), who: elide },
  { id: 'plus_que_parfait', title: 'Plus-que-parfait', blurb: 'j’avais parlé, nous étions entrés…', verbs: ALL,
    forms: comp('imp'), who: elide },
  { id: 'futur_anterieur', title: 'Futur antérieur', blurb: 'j’aurai fini, elles seront arrivées…', verbs: ALL,
    forms: comp('fut'), who: elide },
  { id: 'conditionnel_passe', title: 'Conditionnel passé', blurb: 'j’aurais vendu…', verbs: ALL,
    forms: comp('cond'), who: elide },
  { id: 'subjonctif', title: 'Subjonctif présent', blurb: 'que je parle, que nous finissions…', verbs: ALL,
    forms: (v, i) => [longStem(v) + SUBJ[i]], who: que },
  { id: 'subjonctif_passe', title: 'Subjonctif passé', blurb: 'que j’aie parlé…', verbs: ALL,
    forms: comp('subj'), who: que },
  { id: 'passe_simple', title: 'Passé simple (literary)', blurb: 'il parla, ils finirent…', verbs: ALL,
    forms: (v, i) => [group(v) === 'er' ? attach(root(v), PS_ER[i]) : root(v) + PS_IR[i]], who: elide },
  { id: 'passe_anterieur', title: 'Passé antérieur (literary)', blurb: 'j’eus parlé…', verbs: ALL,
    forms: comp('ps'), who: elide },
  { id: 'imperatif', title: 'Impératif', blurb: 'parle !, finissons !, vendez !', verbs: ALL,
    people: [1, 3, 4], forms: imperative, who: i => ['', '(tu)', '', '(nous)', '(vous)'][i] },
];

export const FR_TENSES: LanguageTenses = {
  lang: 'fr',
  people: [['je'], ['tu'], ['il', 'elle', 'on'], ['nous'], ['vous'], ['ils', 'elles']],
  tenses: TENSES,
};
